import awsS3Service from './awsS3Service';
import { collection, addDoc, doc, setDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../config';

export interface ExcelWorkbook {
  id: string;
  folderId: string;
  workbookName: string;
  sheets: ExcelSheet[];
  columnWidths: { [key: number]: number };
  rowHeights: { [key: number]: number };
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  s3Key?: string;
  s3Bucket?: string;
}

export interface ExcelSheet {
  name: string;
  data: any[][];
  selectedCell: { row: number; col: number } | null;
  selectedRange: { startRow: number; startCol: number; endRow: number; endCol: number } | null;
}

class ExcelAwsS3Service {
  private readonly S3_PREFIX = 'excel-workbooks';
  private readonly FIRESTORE_COLLECTION = 'excelWorkbooks';
  private cache: Map<string, { data: ExcelWorkbook; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    console.log('📊 Excel AWS S3 Service: Initialized with caching');
  }

  /**
   * Save complete Excel workbook to AWS S3
   */
  async saveWorkbook(
    workbook: ExcelWorkbook,
    onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
  ): Promise<{ success: boolean; s3Key?: string; error?: string }> {
    try {
      console.log('💾 Saving Excel workbook to AWS S3...', workbook.id);

      if (!awsS3Service.isConfigured()) {
        console.error('🚨 CRITICAL: AWS S3 not configured! Data will be lost!');
        console.error('❌ Expected variables: REACT_APP_AWS_REGION, REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_S3_BUCKET_NAME');
        console.error('🚨 CRITICAL: Falling back to Firestore - THIS WILL CAUSE DATA LOSS!');
        console.error('🚨 CRITICAL: Configure AWS S3 immediately to prevent data loss!');
        return await this.saveToFirestore(workbook);
      }

      // Create a JSON file from the workbook data
      const workbookData = JSON.stringify(workbook, null, 2);
      const blob = new Blob([workbookData], { type: 'application/json' });
      const file = new File([blob], `${workbook.workbookName}.json`, { type: 'application/json' });

      // Generate S3 key with consistent format for faster loading
      const consistentS3Key = `${this.S3_PREFIX}/${workbook.folderId}/workbook_${workbook.folderId}.json`;
      
      // Upload to S3 with consistent naming
      const fileInfo = await awsS3Service.uploadFileWithKey(
        file,
        consistentS3Key,
        onProgress
      );

      // Update workbook with S3 info
      workbook.s3Key = fileInfo.s3Key;
      workbook.s3Bucket = fileInfo.s3Bucket;

      // Save metadata to Firestore for quick access
      await this.saveWorkbookMetadata(workbook);

      console.log('✅ Excel workbook saved to AWS S3 successfully');
      
      // Invalidate cache
      this.cache.delete(workbook.folderId);
      console.log('🗑️ Cache invalidated for folder:', workbook.folderId);
      
      return { success: true, s3Key: fileInfo.s3Key };

    } catch (error) {
      console.error('❌ Error saving Excel workbook to S3:', error);
      
      // Fallback to Firestore
      console.log('🔄 Falling back to Firestore storage...');
      return await this.saveToFirestore(workbook);
    }
  }

  /**
   * Load workbook directly from S3 without Firestore metadata lookup
   */
  private async loadDirectlyFromS3(s3Key: string): Promise<ExcelWorkbook | null> {
    try {
      console.log('🚀 Loading directly from S3:', s3Key);
      
      // Get file URL directly from S3
      const fileUrl = await awsS3Service.getFileUrl({
        id: s3Key,
        name: 'workbook.json',
        url: null,
        size: 0,
        type: 'application/json',
        uploadedAt: new Date().toISOString(),
        path: s3Key,
        s3Key: s3Key,
        s3Bucket: ''
      });

      if (!fileUrl) {
        console.log('❌ Could not get direct S3 URL');
        return null;
      }

      // Download and parse the workbook
      const response = await fetch(fileUrl);
      if (!response.ok) {
        console.log('❌ Direct S3 download failed:', response.statusText);
        return null;
      }

      const workbookData = await response.json();
      console.log('✅ Direct S3 load successful');
      return workbookData;
    } catch (error) {
      console.log('❌ Direct S3 load error:', error);
      return null;
    }
  }

  /**
   * Load Excel workbook from AWS S3
   */
  async loadWorkbook(folderId: string, workbookId?: string): Promise<ExcelWorkbook | null> {
    try {
      console.log('📂 Loading Excel workbook from AWS S3...', { folderId, workbookId });

      // Check cache first
      const cacheKey = folderId;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        console.log('⚡ Loading from cache (fast!)');
        return cached.data;
      }

      if (!awsS3Service.isConfigured()) {
        console.error('🚨 CRITICAL: AWS S3 not configured! Data will be lost!');
        console.error('❌ Expected variables: REACT_APP_AWS_REGION, REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_S3_BUCKET_NAME');
        console.error('🚨 CRITICAL: Loading from Firestore - DATA MAY BE LOST!');
        console.error('🚨 CRITICAL: Configure AWS S3 immediately to prevent data loss!');
        return await this.loadFromFirestore(folderId, workbookId);
      }

      // Try multiple S3 key patterns for faster loading
      const possibleKeys = [
        `excel-workbooks/${folderId}/workbook_${folderId}.json`,
        `excel-workbooks/${folderId}/latest.json`,
        `excel-workbooks/${folderId}/workbook.json`
      ];

      for (const s3Key of possibleKeys) {
        try {
          console.log('🚀 Trying S3 key:', s3Key);
          const directResult = await this.loadDirectlyFromS3(s3Key);
          if (directResult) {
            console.log('✅ Successfully loaded directly from S3 with key:', s3Key);
            // Cache the result
            this.cache.set(cacheKey, { data: directResult, timestamp: Date.now() });
            return directResult;
          }
        } catch (error) {
          console.log('⚠️ S3 key failed:', s3Key, error);
        }
      }

      // Fallback to metadata approach if direct loads fail
      console.log('🔄 All direct S3 loads failed, trying metadata approach...');
      const metadata = await this.getWorkbookMetadata(folderId, workbookId);
      if (!metadata) {
        console.log('📄 No workbook metadata found');
        return null;
      }

      if (!metadata.s3Key) {
        console.log('📄 No S3 key found, loading from Firestore');
        return await this.loadFromFirestore(folderId, workbookId);
      }

      // Get file URL from S3
      const fileUrl = await awsS3Service.getFileUrl({
        id: metadata.id,
        name: metadata.workbookName,
        url: null,
        size: 0,
        type: 'application/json',
        uploadedAt: metadata.updatedAt,
        path: metadata.s3Key,
        s3Key: metadata.s3Key,
        s3Bucket: metadata.s3Bucket || ''
      });

      if (!fileUrl) {
        console.error('❌ Could not get file URL from S3');
        return await this.loadFromFirestore(folderId, workbookId);
      }

      // Download and parse the workbook
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to download workbook: ${response.statusText}`);
      }

      const workbookData = await response.json();
      console.log('✅ Excel workbook loaded from AWS S3 successfully');
      
      // Cache the result
      this.cache.set(cacheKey, { data: workbookData, timestamp: Date.now() });
      console.log('💾 Workbook cached for faster future access');
      
      return workbookData;

    } catch (error) {
      console.error('❌ Error loading Excel workbook from S3:', error);
      
      // Fallback to Firestore
      console.log('🔄 Falling back to Firestore storage...');
      return await this.loadFromFirestore(folderId, workbookId);
    }
  }

  /**
   * List all workbooks for a folder
   */
  async listWorkbooks(folderId: string): Promise<ExcelWorkbook[]> {
    try {
      console.log('📋 Listing Excel workbooks for folder:', folderId);

      const workbooksQuery = query(
        collection(db, this.FIRESTORE_COLLECTION),
        where('folderId', '==', folderId)
      );

      const querySnapshot = await getDocs(workbooksQuery);
      const workbooks: ExcelWorkbook[] = [];

      querySnapshot.docs.forEach(doc => {
        workbooks.push(doc.data() as ExcelWorkbook);
      });

      console.log(`✅ Found ${workbooks.length} workbooks for folder ${folderId}`);
      return workbooks;

    } catch (error) {
      console.error('❌ Error listing workbooks:', error);
      return [];
    }
  }

  /**
   * Delete Excel workbook from AWS S3 and Firestore
   */
  async deleteWorkbook(workbookId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🗑️ Deleting Excel workbook:', workbookId);

      // Get workbook metadata
      const metadata = await this.getWorkbookMetadataById(workbookId);
      if (!metadata) {
        return { success: false, error: 'Workbook not found' };
      }

      // Delete from S3 if it exists there
      if (metadata.s3Key && awsS3Service.isConfigured()) {
        try {
          await awsS3Service.deleteFile({
            id: metadata.id,
            name: metadata.workbookName,
            url: null,
            size: 0,
            type: 'application/json',
            uploadedAt: metadata.updatedAt,
            path: metadata.s3Key,
            s3Key: metadata.s3Key,
            s3Bucket: metadata.s3Bucket || ''
          });
          console.log('✅ Workbook deleted from S3');
        } catch (s3Error) {
          console.warn('⚠️ Error deleting from S3, continuing with Firestore deletion:', s3Error);
        }
      }

      // Delete from Firestore
      const workbookRef = doc(db, this.FIRESTORE_COLLECTION, workbookId);
      await deleteDoc(workbookRef);

      console.log('✅ Workbook deleted successfully');
      return { success: true };

    } catch (error) {
      console.error('❌ Error deleting workbook:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Save workbook metadata to Firestore (with full data for proper loading)
   */
  private async saveWorkbookMetadata(workbook: ExcelWorkbook): Promise<void> {
    try {
      console.log('💾 Saving workbook metadata to Firestore...');
      console.log('📊 Workbook metadata to save:', {
        id: workbook.id,
        folderId: workbook.folderId,
        sheetsCount: workbook.sheets.length,
        sheetNames: workbook.sheets.map(s => s.name)
      });
      
      // Create a Firestore-compatible version with full data
      const firestoreWorkbook = {
        id: workbook.id,
        folderId: workbook.folderId,
        workbookName: workbook.workbookName,
        version: workbook.version,
        createdAt: workbook.createdAt,
        updatedAt: workbook.updatedAt,
        createdBy: workbook.createdBy,
        updatedBy: workbook.updatedBy,
        s3Key: workbook.s3Key,
        s3Bucket: workbook.s3Bucket,
        // Store full sheet data for proper loading - convert nested arrays to strings
        sheets: workbook.sheets.map(sheet => ({
          name: sheet.name,
          data: JSON.stringify(sheet.data), // Convert 2D array to JSON string
          selectedCell: sheet.selectedCell,
          selectedRange: sheet.selectedRange
        })),
        // Store column widths and row heights as simple objects
        columnWidths: workbook.columnWidths,
        rowHeights: workbook.rowHeights,
        storageType: 's3-with-firestore-backup'
      };
      
      console.log('📊 Firestore metadata data:', {
        id: firestoreWorkbook.id,
        sheetsCount: firestoreWorkbook.sheets.length,
        hasSheets: !!firestoreWorkbook.sheets,
        sheetNames: firestoreWorkbook.sheets.map(s => s.name)
      });
      
      const workbookRef = doc(db, this.FIRESTORE_COLLECTION, workbook.id);
      await setDoc(workbookRef, firestoreWorkbook, { merge: true });
      console.log('✅ Workbook with full data saved to Firestore');
    } catch (error) {
      console.error('❌ Error saving workbook metadata:', error);
      throw error;
    }
  }

  /**
   * Get workbook metadata from Firestore
   */
  private async getWorkbookMetadata(folderId: string, workbookId?: string): Promise<ExcelWorkbook | null> {
    try {
      let workbooksQuery;

      if (workbookId) {
        // Get specific workbook
        const workbookRef = doc(db, this.FIRESTORE_COLLECTION, workbookId);
        const workbookDoc = await getDocs(query(collection(db, this.FIRESTORE_COLLECTION), where('id', '==', workbookId)));
        
        if (workbookDoc.empty) return null;
        return workbookDoc.docs[0].data() as ExcelWorkbook;
      } else {
        // Get latest workbook for folder
        workbooksQuery = query(
          collection(db, this.FIRESTORE_COLLECTION),
          where('folderId', '==', folderId)
        );
      }

      const querySnapshot = await getDocs(workbooksQuery!);
      if (querySnapshot.empty) return null;

      // Return the most recent workbook
      const workbooks = querySnapshot.docs.map(doc => doc.data() as ExcelWorkbook);
      return workbooks.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

    } catch (error) {
      console.error('❌ Error getting workbook metadata:', error);
      return null;
    }
  }

  /**
   * Get workbook metadata by ID
   */
  private async getWorkbookMetadataById(workbookId: string): Promise<ExcelWorkbook | null> {
    try {
      const workbooksQuery = query(
        collection(db, this.FIRESTORE_COLLECTION),
        where('id', '==', workbookId)
      );

      const querySnapshot = await getDocs(workbooksQuery);
      if (querySnapshot.empty) return null;

      return querySnapshot.docs[0].data() as ExcelWorkbook;

    } catch (error) {
      console.error('❌ Error getting workbook metadata by ID:', error);
      return null;
    }
  }

  /**
   * Fallback: Save to Firestore only (with full data for Firestore compatibility)
   */
  private async saveToFirestore(workbook: ExcelWorkbook): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('💾 Saving Excel workbook to Firestore with full data...');
      console.log('📊 Workbook to save:', {
        id: workbook.id,
        folderId: workbook.folderId,
        sheetsCount: workbook.sheets.length,
        sheetNames: workbook.sheets.map(s => s.name)
      });
      
      // Check if we're hitting Firestore quota issues
      const currentTime = new Date();
      const lastErrorTime = localStorage.getItem('firestoreLastError');
      if (lastErrorTime && (currentTime.getTime() - parseInt(lastErrorTime)) < 60000) { // 1 minute cooldown
        console.error('🚨 CRITICAL: Firestore quota exceeded! Data will be lost!');
        console.error('🚨 AWS S3 must be configured to prevent data loss!');
        console.warn('⚠️ Firestore quota exceeded recently, skipping save to prevent further quota issues');
        return { success: false, error: 'CRITICAL: Firestore quota exceeded. Configure AWS S3 immediately to prevent data loss!' };
      }
      
      // Create a Firestore-compatible version with full data
      const firestoreWorkbook = {
        id: workbook.id,
        folderId: workbook.folderId,
        workbookName: workbook.workbookName,
        version: workbook.version,
        createdAt: workbook.createdAt,
        updatedAt: workbook.updatedAt,
        createdBy: workbook.createdBy,
        updatedBy: workbook.updatedBy,
        // Store full sheet data for Firestore fallback - convert nested arrays to strings
        sheets: workbook.sheets.map(sheet => ({
          name: sheet.name,
          data: JSON.stringify(sheet.data), // Convert 2D array to JSON string
          selectedCell: sheet.selectedCell,
          selectedRange: sheet.selectedRange
        })),
        // Store column widths and row heights as simple objects
        columnWidths: workbook.columnWidths,
        rowHeights: workbook.rowHeights,
        storageType: 'firestore-fallback'
      };
      
      console.log('📊 Firestore workbook data:', {
        id: firestoreWorkbook.id,
        sheetsCount: firestoreWorkbook.sheets.length,
        hasSheets: !!firestoreWorkbook.sheets,
        sheetNames: firestoreWorkbook.sheets.map(s => s.name)
      });
      
      const workbookRef = doc(db, this.FIRESTORE_COLLECTION, workbook.id);
      await setDoc(workbookRef, firestoreWorkbook, { merge: true });
      
      console.log('✅ Excel workbook with full data saved to Firestore successfully');
      return { success: true };

    } catch (error) {
      console.error('❌ Error saving to Firestore:', error);
      
      // Handle Firestore quota exceeded error
      if (error instanceof Error && error.message.includes('resource-exhausted')) {
        console.warn('⚠️ Firestore quota exceeded, storing timestamp to prevent further attempts');
        localStorage.setItem('firestoreLastError', new Date().getTime().toString());
        return { success: false, error: 'Firestore quota exceeded. Please configure AWS S3 or try again later.' };
      }
      
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Fallback: Load from Firestore only
   */
  private async loadFromFirestore(folderId: string, workbookId?: string): Promise<ExcelWorkbook | null> {
    try {
      console.log('📂 Loading Excel workbook from Firestore...', { folderId, workbookId });
      console.log('🔍 Querying collection:', this.FIRESTORE_COLLECTION);
      console.log('🔍 Querying for folderId:', folderId);
      
      let workbooksQuery;
      if (workbookId) {
        workbooksQuery = query(
          collection(db, this.FIRESTORE_COLLECTION),
          where('id', '==', workbookId)
        );
        console.log('🔍 Querying by workbookId:', workbookId);
      } else {
        workbooksQuery = query(
          collection(db, this.FIRESTORE_COLLECTION),
          where('folderId', '==', folderId)
        );
        console.log('🔍 Querying by folderId:', folderId);
      }

      const querySnapshot = await getDocs(workbooksQuery);
      console.log(`📊 Found ${querySnapshot.docs.length} workbooks in Firestore`);
      
      // Log all found workbooks for debugging
      querySnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        console.log(`📋 Workbook ${index + 1}:`, {
          id: data.id,
          folderId: data.folderId,
          workbookName: data.workbookName,
          hasSheets: !!data.sheets,
          sheetsCount: data.sheets?.length || 0,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt
        });
      });
      
      if (querySnapshot.empty) {
        console.log('📄 No workbook found in Firestore');
        return null;
      }

      // Get the most recent workbook
      const workbooks = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📋 Workbook data:', { 
          id: data.id, 
          name: data.workbookName, 
          hasSheets: !!data.sheets, 
          sheetsCount: data.sheets?.length || 0,
          storageType: data.storageType,
          updatedAt: data.updatedAt
        });
        return data as ExcelWorkbook;
      });
      
      const workbook = workbooks.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
      console.log('📋 Selected workbook:', { 
        id: workbook.id, 
        name: workbook.workbookName, 
        hasSheets: !!workbook.sheets, 
        sheetsCount: workbook.sheets?.length || 0 
      });
      
      // Check if this is a full workbook (has sheets data) or just metadata
      if (workbook.sheets && workbook.sheets.length > 0) {
        console.log('✅ Full workbook data loaded from Firestore');
        
        // Parse the JSON strings back to arrays
        const parsedWorkbook = {
          ...workbook,
          sheets: workbook.sheets.map(sheet => ({
            ...sheet,
            data: typeof sheet.data === 'string' ? JSON.parse(sheet.data) : sheet.data
          }))
        };
        
        console.log('✅ Parsed workbook sheets data');
        return parsedWorkbook;
      } else {
        console.warn('⚠️ Only metadata available in Firestore, cannot reconstruct full workbook');
        console.log('📋 Available fields:', Object.keys(workbook));
        return null;
      }
    } catch (error) {
      console.error('❌ Error loading from Firestore:', error);
      return null;
    }
  }


  /**
   * Clear old metadata-only data and force fresh save
   */
  async clearOldMetadata(folderId: string): Promise<void> {
    try {
      console.log('🧹 Clearing old metadata-only data for folder:', folderId);
      
      const workbooksQuery = query(
        collection(db, this.FIRESTORE_COLLECTION),
        where('folderId', '==', folderId)
      );
      
      const querySnapshot = await getDocs(workbooksQuery);
      
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        // Check if this is old metadata-only data (has totalSheets but no sheets array)
        if (data.totalSheets && !data.sheets) {
          console.log('🗑️ Deleting old metadata-only document:', docSnapshot.id);
          await deleteDoc(docSnapshot.ref);
        }
      }
      
      console.log('✅ Old metadata cleared');
    } catch (error) {
      console.error('❌ Error clearing old metadata:', error);
    }
  }

  /**
   * Debug method to list all Excel workbooks in S3
   */
  async debugListAllWorkbooks(): Promise<void> {
    try {
      console.log('🔍 Debug: Listing all Excel workbooks in S3...');
      
      if (!awsS3Service.isConfigured()) {
        console.log('❌ AWS S3 not configured');
        return;
      }

      const files = await awsS3Service.listFiles('ettaskmanager', 'excel-workbooks/');
      console.log('📊 Found Excel workbooks in S3:', files.length);
      
      files.forEach((file, index) => {
        console.log(`📄 ${index + 1}. ${file.Key} (${file.Size} bytes, ${file.LastModified})`);
      });
      
      // Also check Firestore metadata
      console.log('🔍 Checking Firestore metadata...');
      const metadata = await this.getAllWorkbookMetadata();
      console.log('📊 Found workbook metadata in Firestore:', metadata.length);
      
      metadata.forEach((meta, index) => {
        console.log(`📄 ${index + 1}. ${meta.id} - ${meta.workbookName} (${meta.sheetsCount} sheets)`);
      });
      
    } catch (error) {
      console.error('❌ Error listing workbooks:', error);
    }
  }

  /**
   * Get all workbook metadata from Firestore
   */
  private async getAllWorkbookMetadata(): Promise<any[]> {
    try {
      const { collection, query, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config');
      
      const metadataQuery = query(collection(db, 'excelWorkbooks'));
      const metadataSnapshot = await getDocs(metadataQuery);
      
      return metadataSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting workbook metadata:', error);
      return [];
    }
  }

  /**
   * Check if AWS S3 is configured
   */
  isConfigured(): boolean {
    return awsS3Service.isConfigured();
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(folderId: string): Promise<{
    totalWorkbooks: number;
    totalSize: number;
    lastUpdated: string | null;
  }> {
    try {
      const workbooks = await this.listWorkbooks(folderId);
      
      return {
        totalWorkbooks: workbooks.length,
        totalSize: workbooks.reduce((total, wb) => total + (wb.s3Key ? 1 : 0), 0), // Count S3 stored workbooks
        lastUpdated: workbooks.length > 0 ? workbooks[0].updatedAt : null
      };
    } catch (error) {
      console.error('❌ Error getting storage stats:', error);
      return { totalWorkbooks: 0, totalSize: 0, lastUpdated: null };
    }
  }
}

export default new ExcelAwsS3Service();
